'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useLayoutEffect, useRef } from 'react';
import { TTS_EMOTIONS } from '@/app/constants';
import { useClickOutside } from '@/app/hooks';
import type { EmotionType } from '@/app/types';

interface EmotionSelectorPopoverProps {
  anchorRect: DOMRect;
  selectedEmotionId?: string;
  selectedEmotionStyles?: {
    backgroundColor: string;
    textColor: string;
  };
  onSelect: (emotion: EmotionType) => void;
  onClose: () => void;
  shouldCloseOnOutside?: (target: Node) => boolean;
}

export function EmotionSelectorPopover({
  anchorRect,
  selectedEmotionId,
  selectedEmotionStyles,
  onSelect,
  onClose,
  shouldCloseOnOutside,
}: EmotionSelectorPopoverProps) {
  const t = useTranslations();
  const popoverRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!popoverRef.current) return;
    popoverRef.current.style.top = `${anchorRect.bottom + 6}px`;
    popoverRef.current.style.left = `${anchorRect.left}px`;
  }, [anchorRect]);

  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (shouldCloseOnOutside && !shouldCloseOnOutside(target)) {
        return;
      }

      onClose();
    },
    [onClose, shouldCloseOnOutside],
  );

  useClickOutside(popoverRef, handleClickOutside);

  return (
    <div ref={popoverRef} className="fixed z-50 rounded-8 bg-system-bg02 p-3 shadow-lg">
      <div className="grid grid-cols-2 gap-2">
        {TTS_EMOTIONS.map((emotion) => {
          const isActive = selectedEmotionId === emotion.id;

          return (
            <button
              key={emotion.id}
              type="button"
              className={`flex h-8 items-center justify-center rounded-8 px-3 text-sm transition-colors ${
                isActive
                  ? ''
                  : 'border border-system-white08 text-system-white/60 hover:border-system-white24 hover:text-system-white'
              }`}
              style={
                isActive && selectedEmotionStyles
                  ? {
                      backgroundColor: selectedEmotionStyles.backgroundColor,
                      color: selectedEmotionStyles.textColor,
                      border: `1px solid ${selectedEmotionStyles.backgroundColor}`,
                    }
                  : undefined
              }
              onClick={() => {
                onSelect(emotion);
                onClose();
              }}
            >
              {t(emotion.labelKey)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
